import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { CompanySelectionService } from '@app/core/services/helpers/company-selection.service';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { SortEvent, TableActions } from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { selectIsMobile } from '@app/state/settings/settings.selectors';
import { TabViewItem } from '@interfaces/a-organizar/tabViewItem';
import { User } from '@interfaces/a-organizar/user';
import { Option } from '@interfaces/common/option';
import { Pagination, TablePagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { UserService } from '@services/http/user.service';
import { UsersHttpService } from '@services/http/users/users-http.service';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { ActiveFilterConfig } from '@src/app/shared/interfaces/active-filter.interface';
import { forkJoin, map, Observable, of, Subject, Subscription, catchError, takeUntil } from 'rxjs';
import {
  buildUserActiveFilters,
  DEFAULT_USER_LIST_FILTERS,
  USER_PASSWORD_OPTIONS,
  USER_ROLE_OPTIONS,
  USER_STATUS_OPTIONS,
  UserListFilters,
} from './components/user-filter/user-filter.component';
import {
  UserSummaryCardKey,
  UserSummaryCounts,
} from './components/user-summary-cards/user-summary-cards.component';

@Component({
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  standalone: false,
})
export class UserListComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  isMobile: boolean = false;
  /** Listagem em andamento: começa verdadeiro para a tabela abrir no esqueleto, não em "Nenhum item". */
  loading = true;
  /** A primeira listagem já chegou: daí em diante o recarregamento usa o loading global. */
  private usersLoadedOnce = false;
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  tabViewItems: TabViewItem[] = [];
  tabViewActiveIndex = 0;
  users: User[] = [];
  paginationRequest: TablePagination = {
    Page: 0,
    PageSize: 10,
  };
  pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };
  search: string = '';
  userPermissions = Permissions.User;
  /**
   * Empresas do filtro (várias, card 3697). Quem guarda a seleção é o `app-select-company` em
   * `page-filter`: ele lê e grava o `companyId` da URL e o estado global, como nas outras listas.
   */
  selectedCompanyIds: string[] = [];

  /** Status do filtro: todos (padrão), só ativos ou só inativados. Vai para a API como `Status`. */
  statusFilter: 'all' | 'active' | 'inactive' = 'all';
  /** Situação da senha: qualquer (''), definida, pendente ou com o link vencido. */
  passwordFilter: '' | 'set' | 'pending' | 'link-expired' = '';
  /** Perfil (nome do papel no backend) ou '' para qualquer. */
  roleFilter = '';

  readonly statusOptions = USER_STATUS_OPTIONS;
  readonly passwordOptions = USER_PASSWORD_OPTIONS;
  readonly roleOptions = USER_ROLE_OPTIONS;

  /** Painel de filtros (funil da tabela) aberto. */
  filterIsOpen = false;
  /** Chips dos filtros aplicados; recalculados só quando os filtros mudam. */
  activeFilterConfigs: ActiveFilterConfig[] = [];
  /** Filtros aplicados, como objeto estável para o painel (o getter criaria um objeto novo a cada checagem). */
  appliedFilters: UserListFilters = { ...DEFAULT_USER_LIST_FILTERS };
  /** Números dos cards de resumo (empresas e busca aplicadas); `null` enquanto carregam. */
  readonly summaryCounts = signal<UserSummaryCounts | null>(null);
  /** Contagem dos cards em andamento (cancelada quando outra começa). */
  private summarySubscription?: Subscription;
  /** Evita que a primeira leitura da URL seja tratada como "nada mudou". */
  private filtersLoaded = false;
  constructor(
    translateService: TranslateService,
    private userService: UserService,
    private usersHttpService: UsersHttpService,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private toast: ToastHelperService,
    private companySelection: CompanySelectionService,
    store: Store<AppState>
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.verifyIsMobile();
    this.breadcrumbs = [
      {
        label: this.t(this.tk.User.Administration),
        routerLink: '/',
      },
      {
        label: this.t(this.tk.User.Users),
        routerLink: '/dashboard/user/list',
      },
    ];

    this.tabViewItems = [
      {
        title: this.t(this.tk.User.All),
        count: 0,
        tabindex: 0,
        content: '',
      },
      {
        title: this.t(this.tk.User.Actives),
        count: 0,
        tabindex: 1,
        content: '',
      },
      {
        title: this.t(this.tk.User.Inactives),
        count: 0,
        tabindex: 2,
        content: '',
      },
      {
        title: this.t(this.tk.User.Disableds),
        count: 0,
        tabindex: 3,
        content: '',
      },
      {
        title: this.t(this.tk.User.Pendings),
        count: 0,
        tabindex: 4,
        content: '',
      },
    ];

    // Seleção inicial pela mesma prioridade do seletor (URL > estado global), para a primeira busca já
    // sair filtrada em vez de buscar tudo e refazer quando o seletor carregar.
    this.selectedCompanyIds = this.companySelection.resolveInitialCompanyIds(
      this.route.snapshot.queryParams
    );

    // A busca vive na URL (`filter`): abrir um link, recarregar ou voltar reaplica o mesmo texto.
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.applySearchFromQueryParams(params);
    });
  }

  /**
   * Lê a busca da URL e recarrega a lista quando ela mudou. A navegação disparada pela própria tela
   * (mesmo texto) não faz uma segunda requisição.
   */
  private applySearchFromQueryParams(params: Params): void {
    const search = (params['filter'] as string) || '';
    const status = this.pickOption(
      this.statusOptions,
      params['status'],
      'all'
    ) as typeof this.statusFilter;
    const password = this.pickOption(
      this.passwordOptions,
      params['password'],
      ''
    ) as typeof this.passwordFilter;
    const role = this.pickOption(this.roleOptions, params['role'], '');
    const isFirstLoad = !this.filtersLoaded;
    const changed =
      search !== this.search ||
      status !== this.statusFilter ||
      password !== this.passwordFilter ||
      role !== this.roleFilter;

    this.filtersLoaded = true;
    if (!isFirstLoad && !changed) {
      return;
    }

    const searchChanged = isFirstLoad || search !== this.search;
    this.search = search;
    this.statusFilter = status;
    this.passwordFilter = password;
    this.roleFilter = role;
    this.refreshFilterChips();
    this.paginationRequest = { ...this.paginationRequest, Page: 0 };
    this.getUsers();
    if (searchChanged) this.loadSummary();
  }

  /** Valor da URL, se for uma das opções do filtro; senão o padrão. */
  private pickOption(options: Option[], raw: unknown, fallback: string): string {
    const value = typeof raw === 'string' ? raw : '';
    return options.some(option => option.value === value) ? value : fallback;
  }

  /**
   * Grava busca e filtros na URL, sem empilhar histórico (o padrão some da URL); as empresas o seletor
   * grava sozinho.
   */
  private syncSearchToUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        filter: this.search?.trim() || null,
        status: this.statusFilter === 'all' ? null : this.statusFilter,
        password: this.passwordFilter || null,
        role: this.roleFilter || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Algum filtro além do padrão (busca, status, senha ou perfil). */
  get hasActiveFilters(): boolean {
    return !!this.search || this.activeFilterConfigs.length > 0;
  }

  /** Filtros do painel como estão aplicados agora. */
  get currentFilters(): UserListFilters {
    return { status: this.statusFilter, password: this.passwordFilter, role: this.roleFilter };
  }

  /** Card do resumo cujo filtro está aplicado (destaque), ou `null`. */
  get selectedSummaryCard(): UserSummaryCardKey | null {
    if (this.roleFilter) return null;
    if (this.statusFilter === 'inactive' && !this.passwordFilter) return 'inactive';
    if (this.statusFilter !== 'active') return null;
    if (this.passwordFilter === 'pending') return 'noPassword';
    if (this.passwordFilter === 'link-expired') return 'passwordLinkExpired';
    return this.passwordFilter ? null : 'active';
  }

  /** Aplica os filtros do painel: volta para a primeira página, grava na URL e refaz a busca. */
  handleFilter(filters: UserListFilters): void {
    this.statusFilter = filters.status;
    this.passwordFilter = filters.password;
    this.roleFilter = filters.role;
    this.refreshFilterChips();
    this.paginationRequest = { ...this.paginationRequest, Page: 0 };
    this.syncSearchToUrl();
    this.getUsers();
  }

  /** Card do resumo: aplica o filtro dele (clicar no card já aplicado volta para todos). */
  handleSummaryCard(key: UserSummaryCardKey): void {
    if (this.selectedSummaryCard === key) {
      this.handleFilter({ ...DEFAULT_USER_LIST_FILTERS });
      return;
    }
    const byCard: Record<UserSummaryCardKey, UserListFilters> = {
      active: { status: 'active', password: '', role: '' },
      inactive: { status: 'inactive', password: '', role: '' },
      noPassword: { status: 'active', password: 'pending', role: '' },
      passwordLinkExpired: { status: 'active', password: 'link-expired', role: '' },
    };
    this.handleFilter(byCard[key]);
  }

  /** Chip removido: aquele filtro volta ao padrão. */
  onRemoveFilter(propertyName: string): void {
    const next = { ...this.currentFilters };
    if (propertyName === 'status') next.status = 'all';
    if (propertyName === 'password') next.password = '';
    if (propertyName === 'role') next.role = '';
    this.handleFilter(next);
  }

  /** Limpa status, senha e perfil (as empresas e a busca ficam como estão). */
  clearListFilters(): void {
    this.handleFilter({ ...DEFAULT_USER_LIST_FILTERS });
  }

  /** Recalcula os chips só quando os filtros mudam (array novo a cada checagem faria a tela redesenhar à toa). */
  private refreshFilterChips(): void {
    this.appliedFilters = this.currentFilters;
    this.activeFilterConfigs = buildUserActiveFilters(this.currentFilters);
  }

  /**
   * Números dos cards: quatro consultas de uma linha só (`PageSize=1`) na mesma API da lista, com as empresas e a
   * busca da tela, lendo o `total`. É contagem, não cálculo de negócio; se crescer, vira endpoint de resumo.
   */
  private loadSummary(): void {
    // Troca de busca ou empresa no meio da contagem: a anterior é cancelada para não pintar número de filtro antigo.
    this.summarySubscription?.unsubscribe();
    this.summaryCounts.set(null);
    const count = (extra: Record<string, string>) =>
      this.userService
        .getUsersList(
          {
            Page: 1,
            PageSize: 1,
            CompanyIds: this.companyIdsForRequest(),
            ...extra,
          } as TablePagination,
          this.search
        )
        .pipe(
          map(response => (response?.total as number) ?? 0),
          catchError(() => of(0))
        );

    this.summarySubscription = forkJoin({
      active: count({ Status: 'active' }),
      inactive: count({ Status: 'inactive' }),
      noPassword: count({ Status: 'active', PasswordStatus: 'pending' }),
      passwordLinkExpired: count({ Status: 'active', PasswordStatus: 'link-expired' }),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(counts => this.summaryCounts.set(counts));
  }

  /**
   * Empresas escolhidas no filtro. O seletor também emite ao carregar as opções: a mesma seleção não
   * refaz a busca.
   */
  handleCompaniesChange(options: Option[] | null): void {
    const ids = (options ?? []).map(option => String(option.value));
    const sameSelection =
      ids.length === this.selectedCompanyIds.length &&
      ids.every(id => this.selectedCompanyIds.includes(id));
    if (sameSelection) {
      return;
    }
    this.selectedCompanyIds = ids;
    this.paginationRequest = { ...this.paginationRequest, Page: 0 };
    this.getUsers();
    this.loadSummary();
  }

  handleTabView(tabIndex: number): void {
    this.tabViewActiveIndex = tabIndex;
  }

  getUsers(): void {
    // Primeira carga: a tabela mostra o esqueleto, sem o loading global por cima.
    // Recarregamentos (filtro de empresa, busca, página, ordenação) seguem com o loading global.
    const showGlobalLoading = this.usersLoadedOnce;
    if (showGlobalLoading) this.utilsService.setIsLoading(true);
    this.loading = true;
    this.userService
      .getUsersList(
        {
          ...this.paginationRequest,
          Page: (this.paginationRequest?.Page || 0) + 1,
          // Vai como CompanyIds=a&CompanyIds=b; lista longa vira POST pelo LongQueryToPostInterceptor.
          CompanyIds: this.companyIdsForRequest(),
          Status: this.statusFilter,
          PasswordStatus: this.passwordFilter || undefined,
          Role: this.roleFilter || undefined,
        } as TablePagination,
        this.search
      )
      .subscribe({
        next: response => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.usersLoadedOnce = true;
          this.loading = false;
          if (response) {
            const users = response?.data || [];
            this.users = users.map((user: User) => ({
              ...user,
              name: `${user.firstName} ${user.surName}`,
            }));

            const { total, currentPage, pages, itemsPerPage } = response;
            this.pagination = {
              total: total as number,
              currentPage: currentPage as number,
              pages: pages as number,
              itemsPerPage: itemsPerPage as number,
            };
          }
        },
        error: error => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.usersLoadedOnce = true;
          this.loading = false;
          this.toast.showToastError(
            this.t(this.tk.User.UserErrorLoadingUsers),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  /**
   * Empresas enviadas à API. Todas marcadas (ou nenhuma) = sem filtro: mandar a lista inteira esconderia
   * quem não tem empresa (ADMIN, TENANT, parceiro) e deixaria a URL enorme sem mudar o resultado.
   */
  private companyIdsForRequest(): string[] {
    const available = this.companySelection.availableCompanyIds();
    const allSelected =
      available.length > 0 && available.every(id => this.selectedCompanyIds.includes(id));
    return allSelected ? [] : this.selectedCompanyIds;
  }

  /** Novo usuário: com uma empresa só no filtro, o cadastro já abre nela. */
  addUser(): void {
    if (this.selectedCompanyIds.length === 1)
      this.router.navigate([`dashboard/user/register/company/${this.selectedCompanyIds[0]}`]);
    else this.router.navigate(['dashboard/user/register']);
  }

  handleSearch(search: string = ''): void {
    // Mesmo texto que vai para a URL: sem isso, espaço nas pontas parece "filtro novo" na volta da navegação.
    const next = (search ?? '').trim();
    if (next === this.search) {
      return;
    }
    this.search = next;
    this.paginationRequest = { ...this.paginationRequest, Page: 0 };
    this.syncSearchToUrl();
    this.getUsers();
    this.loadSummary();
  }

  handleTableAction(tableActions: TableActions<User>): void {
    switch (tableActions.action) {
      case 'delete':
        tableActions.items.forEach((user: User, iUser: number) => {
          this.utilsService.setIsLoading(true);
          this.removeUser(user).subscribe({
            next: () => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastSuccess(
                'Usuário inativado. Ele não entra mais na plataforma e aparece como Inativo na lista.',
                ''
              );
              if (iUser === tableActions.items.length - 1) this.reloadAfterStatusChange();
            },
            error: error => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastError(
                this.t(this.tk.User.UserErrorRemovingUser),
                error?.errors?.length
                  ? error.errors[0].message
                  : this.t(this.tk.General.RequestError)
              );
            },
          });
        });

        break;
      case 'reactivate':
        tableActions.items.forEach((user: User) => {
          this.utilsService.setIsLoading(true);
          this.usersHttpService.reactivateUser(user.id).subscribe({
            next: () => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastSuccess(
                'Usuário reativado. Ele já pode entrar de novo na plataforma.',
                ''
              );
              this.reloadAfterStatusChange();
            },
            error: error => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastError(
                'Não foi possível reativar o usuário',
                error?.errors?.length
                  ? error.errors[0].message
                  : this.t(this.tk.General.RequestError)
              );
            },
          });
        });
        break;
      default:
        break;
    }
  }

  /** Inativou ou reativou: a linha muda de status e os números dos cards também. */
  private reloadAfterStatusChange(): void {
    this.getUsers();
    this.loadSummary();
  }

  removeUser(user: User): Observable<User | null> {
    return this.userService.deleteUser(user.id);
  }

  handleTablePagination(pagination: PaginatorState) {
    // Preserves existing sortColumn and sortDirection when pagination changes
    // Cast is needed because TablePagination interface doesn't include these properties,
    // but they are passed dynamically to the API and work correctly
    this.paginationRequest = {
      ...this.paginationRequest,
      Page: pagination.page,
      PageSize: pagination.rows,
    } as TablePagination;
    this.getUsers();
  }

  handleTableSort({ field, direction }: SortEvent): void {
    if (!field || !direction) {
      return;
    }

    // Mapeia o field do evento para o sortField correto da API
    const fieldToSortColumnMap: { [key: string]: string } = {
      name: 'firstName',
      email: 'email',
      company: 'company.corporateName',
      tenant: 'tenant.name',
      lastLogin: 'lastLogin',
    };

    const sortColumn = fieldToSortColumnMap[field] || field;
    const sortDirection = direction?.toUpperCase() || 'ASC';

    // Cast is needed because TablePagination interface doesn't include sortColumn and sortDirection,
    // but they are passed dynamically to the API and work correctly
    this.paginationRequest = {
      ...this.paginationRequest,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    } as TablePagination;

    this.getUsers();
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }

  verifyIsMobile() {
    if (this.store) {
      this.store.select(selectIsMobile).subscribe(isMobile => (this.isMobile = isMobile));
    }
  }
}
